import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, BookOpen, CheckCircle2, ChevronDown, Lock, PlayCircle } from 'lucide-react';
import { useAuth } from '../components/auth/AuthContext';
import { getCourseWithContent, getEnrollment, getCompletedLessonIds, markLessonComplete, enrollInCourse } from '../lib/lms';
import { maybeIssueCertificate } from '../lib/certificates';
import QuizPanel from '../components/learn/QuizPanel';
import AssignmentPanel from '../components/learn/AssignmentPanel';
import LessonNotes from '../components/learn/LessonNotes';
import LessonResources from '../components/learn/LessonResources';
import DiscussionPanel from '../components/learn/DiscussionPanel';
import { toEmbedUrl, type LmsCourse } from '../types/lms';
import { PageHeader, Panel, PanelHead, Stamp } from '../components/lms/ui';
import LearningPath from '../components/lms/LearningPath';
import { LearnSkeleton } from '../components/lms/Skeletons';

const publicCoursePath = (id: string) => {
  if (id === 'fem') return '/fem';
  if (id === 'zero2export') return '/zero2export';
  if (id === 'emp') return '/emp';
  if (id === 'c2c') return '/campus2container';
  if (id.startsWith('wa-')) return '/whatsappclass';
  return `/course/${id}`;
};

type DeskTab = 'notes' | 'discussion' | 'assessments';

const Learn: React.FC = () => {
  const { id = '' } = useParams();
  const { user } = useAuth();
  const [course, setCourse] = useState<LmsCourse | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [completed, setCompleted] = useState<Set<string>>(new Set());
  const [current, setCurrent] = useState<{ moduleId: string; lessonId: string } | null>(null);
  const [openModules, setOpenModules] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [certId, setCertId] = useState<string | null>(null);
  const [tab, setTab] = useState<DeskTab>('notes');

  const allLessons = useMemo(() => course?.modules.flatMap((m) => m.lessons.map((l) => ({ ...l, moduleId: m.id, moduleTitle: m.title }))) ?? [], [course]);
  const currentLesson = useMemo(() => allLessons.find((l) => l.id === current?.lessonId) ?? allLessons[0] ?? null, [allLessons, current]);
  const progressPct = allLessons.length === 0 ? 0 : Math.round((completed.size / allLessons.length) * 100);

  useEffect(() => {
    if (!user || !course || !isEnrolled || allLessons.length === 0 || progressPct < 100 || certId) return;
    void maybeIssueCertificate(user.id, course.id).then((result) => {
      if (result) {
        setCertId(result.certificate.id);
        if (result.created) {
          void import('../lib/notify').then(({ sendCompletionEmail }) =>
            sendCompletionEmail({ toName: user.name, courseTitle: course.title, certCode: result.certificate.code })
          );
        }
      }
    }).catch(() => null);
  }, [user, course, isEnrolled, allLessons.length, progressPct, certId]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      const c = await getCourseWithContent(id);
      if (cancelled) return;
      setCourse(c);
      if (c && c.modules.length > 0) {
        const lessons = c.modules.flatMap((m) => m.lessons.map((l) => ({ ...l, moduleId: m.id })));
        let resumeId: string | null = null;
        try {
          resumeId = localStorage.getItem(`lsem-last-lesson-${c.id}`);
        } catch {
          resumeId = null;
        }
        const resume = lessons.find((l) => l.id === resumeId) ?? lessons[0];
        if (resume) {
          setCurrent({ moduleId: resume.moduleId, lessonId: resume.id });
          setOpenModules(new Set([resume.moduleId]));
        }
      }
      if (c && user) {
        const enr = await getEnrollment(user.id, c.id).catch(() => null);
        if (cancelled) return;
        setIsEnrolled(!!enr && (enr as { status?: string }).status !== 'revoked');
        const ids = c.modules.flatMap((m) => m.lessons.map((l) => l.id));
        const done = await getCompletedLessonIds(user.id, ids).catch(() => new Set<string>());
        if (!cancelled) setCompleted(done);
      } else {
        setIsEnrolled(false);
      }
      setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [id, user]);

  const toggleModule = (mid: string) => {
    setOpenModules((prev) => {
      const next = new Set(prev);
      if (next.has(mid)) next.delete(mid);
      else next.add(mid);
      return next;
    });
  };

  const handleToggleComplete = async () => {
    if (!user || !currentLesson) return;
    setSaving(true);
    const next = !completed.has(currentLesson.id);
    await markLessonComplete(user.id, currentLesson.id, next).catch(() => null);
    setCompleted((prev) => {
      const s = new Set(prev);
      if (next) s.add(currentLesson.id);
      else s.delete(currentLesson.id);
      return s;
    });
    setSaving(false);
  };

  const selectLesson = (moduleId: string, lessonId: string) => {
    setCurrent({ moduleId, lessonId });
    if (course) {
      try {
        localStorage.setItem(`lsem-last-lesson-${course.id}`, lessonId);
      } catch {
        /* ignore */
      }
    }
  };

  const goDelta = (d: number) => {
    if (!currentLesson) return;
    const idx = allLessons.findIndex((l) => l.id === currentLesson.id);
    const nxt = allLessons[idx + d];
    if (nxt) {
      selectLesson(nxt.moduleId, nxt.id);
      setOpenModules((prev) => new Set(prev).add(nxt.moduleId));
    }
  };

  const handleEnroll = async () => {
    if (!user || !course) return;
    setEnrolling(true);
    try {
      await enrollInCourse(user.id, course.id);
      setIsEnrolled(true);
    } finally {
      setEnrolling(false);
    }
  };

  if (loading) {
    return <LearnSkeleton />;
  }

  if (!course) {
    return (
      <div className="rounded-2xl border border-rule bg-white px-6 py-16 text-center shadow-sm">
        <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
          <BookOpen className="h-5 w-5" strokeWidth={1.7} />
        </span>
        <p className="mt-4 text-[16px] font-semibold text-ink">Programme not found</p>
        <p className="mt-1 text-[13px] text-ink-2">This classroom may have been unpublished or moved.</p>
        <Link to="/courses" className="lms-btn-secondary mt-5">
          <ArrowLeft className="h-4 w-4" strokeWidth={1.7} /> Programme register
        </Link>
      </div>
    );
  }

  if (!isEnrolled) {
    return (
      <div>
        <Link to={`/course/${course.id}`} className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-slate-500 transition-colors hover:text-blue-700">
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.7} /> Programme details
        </Link>

        <div className="mt-5 grid gap-7 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
          <div>
            <PageHeader
              eyebrow="Classroom preview"
              title={course.title}
              lede={course.tagline || course.description}
            />
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <Stamp tone="neutral">{course.category}</Stamp>
              <span className="inline-flex items-center gap-1.5 text-[12px] text-ink-2">
                <BookOpen className="h-3.5 w-3.5" strokeWidth={1.7} />
                {course.modules.length} modules · {allLessons.length} lessons · {course.duration}
              </span>
            </div>

            <Panel className="mt-6">
              <PanelHead title="What you will study" />
              <div className="divide-y divide-rule">
                {course.modules.map((m, mi) => (
                  <div key={m.id} className="grid gap-3 px-5 py-4 sm:grid-cols-[36px_minmax(0,1fr)]">
                    <span className="font-mono text-[10px] font-semibold text-blue-600">{String(mi + 1).padStart(2, '0')}</span>
                    <div>
                      <p className="text-[14px] font-semibold leading-5 text-ink">{m.title}</p>
                      <ul className="mt-2 space-y-1.5">
                        {m.lessons.map((l) => (
                          <li key={l.id} className="flex items-center gap-2 text-[12.5px] text-ink-2">
                            <PlayCircle className="h-3.5 w-3.5 flex-none text-blue-400" strokeWidth={1.7} /> {l.title}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </Panel>
          </div>

          <aside className="lg:sticky lg:top-24">
            <Panel className="overflow-hidden">
              <div className="border-b border-rule bg-gradient-to-br from-blue-50 to-sky-50 px-5 py-5">
                <p className="font-mono text-[9px] uppercase tracking-[0.13em] text-blue-700">Preview access</p>
                <p className="mt-2 text-[15px] font-semibold leading-6 text-ink">The syllabus is open. The classroom is locked until you enroll.</p>
              </div>
              <div className="space-y-3 px-5 py-5">
                <p className="text-[12.5px] leading-5 text-ink-2">
                  Enrollment unlocks every lesson, private notes, assessments, instructor Q&amp;A and your certificate.
                </p>
                {course.price_value === 0 ? (
                  <button type="button" onClick={handleEnroll} disabled={enrolling} className="lms-btn-primary w-full">
                    {enrolling ? 'Enrolling…' : 'Enroll free'}
                  </button>
                ) : (
                  <Link to={publicCoursePath(course.id)} className="lms-btn-primary w-full">
                    Enroll — ₦{course.price_value.toLocaleString('en-NG')}
                  </Link>
                )}
                <Link to={publicCoursePath(course.id)} className="lms-btn-secondary w-full">
                  View programme details
                </Link>
              </div>
            </Panel>
          </aside>
        </div>
      </div>
    );
  }

  const embed = currentLesson ? toEmbedUrl(currentLesson.video_url) : null;
  const idx = currentLesson ? allLessons.findIndex((l) => l.id === currentLesson.id) : -1;

  return (
    <div>
      <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-slate-500 transition-colors hover:text-blue-700">
        <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.7} /> Student overview
      </Link>

      <header className="mt-4 overflow-hidden rounded-2xl border border-rule bg-white shadow-[0_16px_45px_-40px_rgba(15,40,90,0.35)]">
        <div className="relative p-5 sm:p-6">
          <div aria-hidden="true" className="absolute inset-y-0 right-0 hidden w-1/3 bg-gradient-to-l from-blue-50/80 to-transparent sm:block" />
          <div className="relative min-w-0">
            <div className="flex flex-wrap items-center gap-2.5">
              <Stamp tone={progressPct >= 100 ? 'cleared' : 'issued'}>{progressPct >= 100 ? 'Complete' : 'In progress'}</Stamp>
              <span className="font-mono text-[8.5px] uppercase tracking-[0.12em] text-ink-2">
                LSEM-{course.id.replace(/[^a-zA-Z0-9]/g, '').slice(0, 6).toUpperCase()}
              </span>
              <span className="hidden h-3 w-px bg-rule sm:block" />
              <span className="hidden font-mono text-[8.5px] uppercase tracking-[0.12em] text-ink-2 sm:block">
                {course.modules.length} modules · {allLessons.length} lessons
              </span>
            </div>
            <h1 className="mt-2.5 max-w-[34ch] text-[clamp(1.45rem,2.4vw,2rem)] font-semibold leading-tight tracking-[-0.03em] text-ink">
              {course.title}
            </h1>
            <LearningPath
              completed={completed.size}
              total={allLessons.length}
              className="mt-5 max-w-[560px]"
            />
          </div>
        </div>
      </header>

      <div className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start">
        <div className="min-w-0 space-y-5">
          <section className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-950 shadow-xl shadow-slate-900/10">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-white/10 px-4 py-3">
              <span className="truncate font-mono text-[8.5px] uppercase tracking-[0.12em] text-white/55">
                {currentLesson?.moduleTitle}
              </span>
              <span className="font-mono text-[8.5px] uppercase tracking-[0.12em] text-white/45">
                Lesson {idx + 1} / {allLessons.length}
              </span>
            </div>
            <div className="aspect-video bg-slate-950">
              {embed ? (
                embed.includes('youtube.com/embed') || embed.includes('vimeo') ? (
                  <iframe src={embed} title={currentLesson?.title} className="h-full w-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                ) : (
                  <video src={embed} controls className="h-full w-full" />
                )
              ) : (
                <div className="flex h-full flex-col items-center justify-center px-8 text-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white/70">
                    <PlayCircle className="h-8 w-8" strokeWidth={1.2} />
                  </span>
                  <p className="mt-4 text-[14px] font-medium text-white">{currentLesson?.title}</p>
                  <p className="mt-1.5 font-mono text-[8.5px] uppercase tracking-[0.13em] text-white/40">Lesson media coming soon</p>
                </div>
              )}
            </div>
          </section>

          <Panel>
            <div className="p-5 sm:p-6">
              <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 font-mono text-[8.5px] uppercase tracking-[0.11em] text-ink-2">
                <span className="text-blue-600">{currentLesson?.moduleTitle}</span>
                <span aria-hidden="true">/</span>
                <span>Lesson {idx + 1} of {allLessons.length}</span>
                {currentLesson?.duration && <><span aria-hidden="true">/</span><span>{currentLesson.duration}</span></>}
              </div>
              <h2 className="mt-2 text-[22px] font-semibold leading-snug tracking-[-0.025em] text-ink">{currentLesson?.title}</h2>
              {currentLesson?.content && (
                <p className="mt-2.5 max-w-[72ch] whitespace-pre-wrap text-[13.5px] leading-6 text-ink-2">{currentLesson.content}</p>
              )}
              {currentLesson?.resources && <LessonResources raw={currentLesson.resources} />}

              <div className="mt-6 flex flex-wrap items-center gap-2.5 border-t border-rule pt-4">
                <button
                  type="button"
                  onClick={handleToggleComplete}
                  disabled={saving}
                  className={
                    completed.has(currentLesson?.id ?? '')
                      ? 'lms-btn border border-success/20 bg-success-soft text-success hover:bg-success/10'
                      : 'lms-btn-primary'
                  }
                >
                  <CheckCircle2 className="h-4 w-4" strokeWidth={1.8} />
                  {completed.has(currentLesson?.id ?? '') ? 'Lesson complete' : saving ? 'Saving…' : 'Mark lesson complete'}
                </button>
                <div className="ml-auto flex items-center gap-2">
                  <button type="button" onClick={() => goDelta(-1)} disabled={idx <= 0} className="lms-btn-secondary !px-3">
                    <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.8} /> Prev
                  </button>
                  <button type="button" onClick={() => goDelta(1)} disabled={idx < 0 || idx >= allLessons.length - 1} className="lms-btn-secondary !px-3">
                    Next <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.8} />
                  </button>
                </div>
              </div>
            </div>
          </Panel>

          {certId && (
            <div className="flex flex-col gap-3 rounded-2xl border border-success/20 bg-success-soft px-5 py-4 sm:flex-row sm:items-center">
              <CheckCircle2 className="h-6 w-6 flex-none text-success" strokeWidth={1.7} />
              <div className="flex-1">
                <p className="text-[14px] font-semibold text-success">Programme complete — certificate issued</p>
                <p className="mt-0.5 text-[12px] text-success/80">Print it, or send an employer the public verification code.</p>
              </div>
              <Link to="/certificates" className="lms-btn bg-success text-white shadow-sm hover:bg-success/90">
                View certificate
              </Link>
            </div>
          )}

          {user && currentLesson && (
            <Panel>
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-rule px-3 py-3">
                <div className="flex gap-1 rounded-xl bg-paper p-1" role="tablist" aria-label="Lesson desk">
                  {([
                    ['notes', 'My notes'],
                    ['discussion', 'Questions & answers'],
                    ['assessments', 'Assessments'],
                  ] as [DeskTab, string][]).map(([key, label]) => {
                    const active = tab === key;
                    return (
                      <button
                        key={key}
                        role="tab"
                        aria-selected={active}
                        onClick={() => setTab(key)}
                        className={`rounded-lg px-3 py-2 text-[11.5px] font-semibold transition-all ${
                          active ? 'bg-white text-blue-700 shadow-sm' : 'text-ink-2 hover:text-ink'
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                <span className="hidden font-mono text-[8.5px] uppercase tracking-[0.1em] text-ink-2 sm:block">
                  {tab === 'notes' ? 'Private to you' : tab === 'discussion' ? 'Course thread' : 'Graded work'}
                </span>
              </div>
              <div className={tab === 'notes' ? 'p-0' : 'p-5'}>
                {tab === 'notes' && <LessonNotes userId={user.id} lessonId={currentLesson.id} />}
                {tab === 'discussion' && (
                  <DiscussionPanel
                    courseId={course.id}
                    lessonId={currentLesson.id}
                    lessonTitle={currentLesson.title}
                    userId={user.id}
                    isAdmin={user.role === 'ADMIN'}
                  />
                )}
                {tab === 'assessments' && (
                  <div className="grid gap-5 xl:grid-cols-2">
                    <QuizPanel courseId={course.id} userId={user.id} />
                    <AssignmentPanel courseId={course.id} userId={user.id} />
                  </div>
                )}
              </div>
            </Panel>
          )}
        </div>

        <aside className="space-y-4 lg:sticky lg:top-24">
          <Panel className="overflow-hidden">
            <div className="border-b border-rule px-5 py-4">
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-[13.5px] font-semibold text-ink">Course content</h2>
                <span className="font-mono text-[8.5px] uppercase tracking-[0.1em] text-ink-2">{course.modules.length} modules</span>
              </div>
            </div>
            <div className="max-h-[calc(100vh-14rem)] overflow-y-auto p-2">
              {course.modules.map((m, mi) => {
                const moduleDone = m.lessons.filter((l) => completed.has(l.id)).length;
                const open = openModules.has(m.id);
                return (
                  <div key={m.id} className="mb-1 last:mb-0">
                    <button
                      onClick={() => toggleModule(m.id)}
                      className="flex w-full items-start justify-between gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-blue-50/70"
                      aria-expanded={open}
                    >
                      <span className="min-w-0">
                        <span className="flex items-center gap-2">
                          <span className="font-mono text-[8.5px] text-blue-500">{String(mi + 1).padStart(2, '0')}</span>
                          <span className="text-[12px] font-semibold leading-5 text-ink">{m.title}</span>
                        </span>
                        <span className="mt-0.5 block pl-[26px] font-mono text-[8px] uppercase tracking-[0.1em] text-ink-2">
                          {moduleDone}/{m.lessons.length} complete
                        </span>
                      </span>
                      <ChevronDown className={`mt-0.5 h-4 w-4 flex-none text-ink-2 transition-transform ${open ? 'rotate-180' : ''}`} strokeWidth={1.7} />
                    </button>
                    {open && (
                      <div className="pb-1">
                        {m.lessons.map((l, li) => {
                          const active = l.id === currentLesson?.id;
                          const done = completed.has(l.id);
                          const locked = !l.is_free && !isEnrolled;
                          return (
                            <button
                              key={l.id}
                              onClick={() => !locked && selectLesson(m.id, l.id)}
                              className={`flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left text-[11.5px] transition-colors ${
                                active
                                  ? 'bg-blue-50 text-blue-900'
                                  : 'text-ink-2 hover:bg-slate-50 hover:text-ink'
                              }`}
                            >
                              <span
                                className={`flex h-5 w-5 flex-none items-center justify-center rounded-full font-mono text-[8px] ${
                                  done
                                    ? 'bg-success text-white'
                                    : active
                                      ? 'bg-blue-600 text-white'
                                      : 'border border-rule bg-white text-ink-2'
                                }`}
                              >
                                {done ? <CheckCircle2 className="h-3 w-3" strokeWidth={2.2} /> : locked ? <Lock className="h-2.5 w-2.5" strokeWidth={1.7} /> : String(li + 1).padStart(2, '0')}
                              </span>
                              <span className="min-w-0 flex-1 leading-4">{l.title}</span>
                              {active && <span className="rounded-full bg-blue-600 px-1.5 py-0.5 font-mono text-[7px] font-semibold uppercase tracking-[0.08em] text-white">Now</span>}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Panel>

          <Panel className="p-4">
            <p className="text-[12px] font-semibold text-ink">Need a hand?</p>
            <p className="mt-1 text-[11.5px] leading-5 text-ink-2">The student desk can help with access, assessments or your certificate.</p>
            <a href="mailto:info@lsem.org.ng" className="mt-3 inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-blue-700 hover:text-blue-800">
              Email student support <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.8} />
            </a>
          </Panel>
        </aside>
      </div>
    </div>
  );
};

export default Learn;
